import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { teamMembers, consultationImages, companyInfo } from '../data/mock';
import { Target, Heart, Users, Shield, Award, TrendingUp, ArrowRight, CheckCircle2 } from 'lucide-react';
import ChatWidget from '../components/common/ChatWidget';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-300 rounded-full blur-3xl" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4">
                Über uns
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ihr unabhängiger Partner für Finanzierungen
              </h1>
              <p className="text-xl text-slate-300">
                Seit über 15 Jahren helfen wir Menschen dabei, ihre finanziellen Ziele zu erreichen – transparent, kompetent und kundenorientiert.
              </p>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Unsere Mission</span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6">
                  Finanzierungen einfach machen
                </h2>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Wir glauben, dass jeder Mensch Zugang zu fairen Finanzierungskonditionen haben sollte. Deshalb vergleichen wir für Sie die Angebote von über 500 Banken und finden die Lösung, die wirklich zu Ihnen passt.
                </p>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Als unabhängiger Vermittler sind wir keiner Bank verpflichtet – nur Ihnen. Diese Unabhängigkeit garantiert, dass wir immer in Ihrem besten Interesse handeln.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    'Unabhängige Beratung',
                    'Über 500 Partnerbanken',
                    'Transparente Konditionen',
                    'Persönliche Betreuung'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_a88de29f-7657-4da8-9316-5527c86ee63f/artifacts/e1z8pjtk_%7BE5F41894-0803-4016-8685-D5677BCB3030%7D.png"
                  alt="Finanzanalyse"
                  className="rounded-2xl shadow-2xl w-full h-96 object-cover"
                />
                <div className="absolute -bottom-8 -right-8 bg-blue-600 text-white rounded-2xl p-6 shadow-xl">
                  <p className="text-4xl font-bold">15+</p>
                  <p className="text-blue-100">Jahre Erfahrung</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Unsere Werte</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
                Wofür wir stehen
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: Shield,
                  title: 'Vertrauen',
                  description: 'Transparenz und Ehrlichkeit bilden das Fundament unserer Arbeit.'
                },
                {
                  icon: Target,
                  title: 'Fokus',
                  description: 'Ihre Ziele stehen im Mittelpunkt – wir finden die passende Lösung.'
                },
                {
                  icon: Heart,
                  title: 'Leidenschaft',
                  description: 'Wir brennen für das, was wir tun, und geben stets unser Bestes.'
                },
                {
                  icon: TrendingUp,
                  title: 'Qualität',
                  description: 'Höchste Standards in Beratung, Service und Ergebnissen.'
                }
              ].map((value, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 text-center hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Das Team</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
                Menschen, die für Sie da sind
              </h2>
              <p className="text-slate-600">
                Unser erfahrenes Team aus Finanzexperten steht Ihnen mit Rat und Tat zur Seite.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {teamMembers.map((member) => (
                <div key={member.id} className="group">
                  <div className="relative rounded-2xl overflow-hidden mb-4 md:mb-6">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full aspect-[4/5] sm:aspect-[4/5] object-cover group-hover:scale-105 transition-transform duration-500 max-h-64 sm:max-h-80 md:max-h-none"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-xl font-semibold text-white">{member.name}</h3>
                      <p className="text-blue-300 font-medium">{member.role}</p>
                    </div>
                  </div>
                  <p className="text-slate-600">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-20 bg-slate-900">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { value: '15+', label: 'Jahre Erfahrung' },
                { value: '500+', label: 'Partnerbanken' },
                { value: '10.000+', label: 'Zufriedene Kunden' },
                { value: '500 Mio.€', label: 'Vermitteltes Volumen' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-4xl md:text-5xl font-bold text-blue-400 mb-2">{stat.value}</p>
                  <p className="text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-blue-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Lernen Sie uns kennen
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Vereinbaren Sie ein unverbindliches Beratungsgespräch und überzeugen Sie sich von unserem Service.
            </p>
            <Link to="/anfrage">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
                Jetzt Kontakt aufnehmen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default AboutPage;
